import { Fragment } from 'react';
import { captureException } from '@sentry/react-router';

import { type ComponentLoaderContext } from '~/types';
import { type ComponentStateType } from '~/types/template';
import { invalidDehydratedState } from '~/utils/invalidDehydratedState.ts';

import { ComponentErrorBoundary } from '../components/ComponentErrorBoundary/ComponentErrorBoundary';
import { componentMapClient } from '../page-template/component-map';

import { isGroupComponent } from './isGroupComponent';

export function renderComponents({
  componentLoaderContext,
  components,
  gtmId,
  section,
}: {
  componentLoaderContext: ComponentLoaderContext;
  components: ComponentStateType[] | null;
  // NGF-6128: gtmId was removed from the component context (it lives on the loader response). The
  // offers/grid client components still need it for their clickout props, so it is threaded through
  // the loader response and merged onto the per-component props here.
  gtmId: string;
  section?: string;
}) {
  if (!components) return null;

  return components.map((component, index) => {
    const id = component?.id ?? `failed-component-${index}`;
    // Hoisted so the catch can tag Sentry with the component name (matching
    // ComponentErrorBoundary.onError), falling back to the id if unavailable.
    const name = component?.name;

    try {
      const componentState = component.componentState;
      if (!name) return null;
      const componentObj = componentMapClient[name];
      if (!componentObj) return null;
      const Component = componentObj.Component;

      const componentProps = isGroupComponent(name) ? component : component.componentArgs;

      if (invalidDehydratedState(componentState)) return null;

      const rendered = (
        <ComponentErrorBoundary componentName={name}>
          <Component {...componentLoaderContext} gtmId={gtmId} {...componentProps} dehydratedState={componentState} />
        </ComponentErrorBoundary>
      );

      if (!componentLoaderContext.showComponentOverlay) return <Fragment key={id}>{rendered}</Fragment>;

      return (
        <div data-overlay-name={name} data-overlay-section={section ?? ''} key={id} style={{ display: 'contents' }}>
          {rendered}
        </div>
      );
    } catch (error) {
      captureException(error, { tags: { component: name ?? id } });
      return null;
    }
  });
}
